
import { useSnackbar } from '@components/feedback/GlobalSnackbar';
import { useApiError } from '@hooks/useApiError';
import { CheckCircleOutlined, EmailOutlined, VerifiedUserOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { useResendVerification, useVerifyEmail } from '../useSettingsData';

import styles from './EmailVerificationDialog.module.css';

const RESEND_DELAY = 30;

export interface EmailVerificationDialogProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onVerified: () => void;
}

export const EmailVerificationDialog = ({
  open,
  email,
  onClose,
  onVerified,
}: EmailVerificationDialogProps) => {
  const { show: showSnack } = useSnackbar();
  const resendVerification = useResendVerification();
  const verifyEmail = useVerifyEmail();
  const [otp, setOtp]             = useState<string[]>(Array(6).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending,   setIsSending]   = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [countdown,   setCountdown]   = useState(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(6).fill(null));

  // Reset state each time the dialog opens; OTP was already sent by the parent
  useEffect(() => {
    if (!open) return;
    setOtp(Array(6).fill(''));
    setVerifyError(null);
    setCountdown(RESEND_DELAY);
    const t = setTimeout(() => inputRefs.current[0]?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  // Countdown tick
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown((v) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  const handleResend = async () => {
    if (isSending) return;
    setIsSending(true);
    try {
      await resendVerification.mutateAsync();
      setOtp(Array(6).fill(''));
      setVerifyError(null);
      setCountdown(RESEND_DELAY);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch (err) {
      showSnack(useApiError(err) ?? 'Failed to resend code.', 'error');
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setVerifyError(null);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!digits) return;
    e.preventDefault();
    const next = Array(6).fill('') as string[];
    digits.split('').forEach((d, i) => { next[i] = d; });
    setOtp(next);
    setVerifyError(null);
    inputRefs.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) { setVerifyError('Please enter all 6 digits.'); return; }
    setIsVerifying(true);
    setVerifyError(null);
    try {
      await verifyEmail.mutateAsync(code);
      showSnack('Email verified successfully!', 'success');
      onVerified();
    } catch (err) {
      setVerifyError(useApiError(err) ?? 'Invalid or expired code. Try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setOtp(Array(6).fill(''));
    setVerifyError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{ paper: { sx: { borderRadius: '24px', overflow: 'hidden', width: 448, maxWidth: '95vw', m: '16px' } } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Blue header */}
        <Box sx={(theme) => ({
          bgcolor: theme.palette.primary.main, height: 188,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          px: '32px',
        })}>
          <Box sx={{
            bgcolor: 'white', borderRadius: '16px',
            width: 64, height: 64,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: '16px',
          }}>
            <VerifiedUserOutlined sx={(theme) => ({ fontSize: 28, color: theme.palette.primary.main })} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'white', lineHeight: '28px' }}>
            Verify Your Email
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', mt: '8px', textAlign: 'center' }}>
            Enter the 6-digit code sent to your email
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: '16px', sm: '32px' }, pt: '24px' }}>
          {/* Info card */}
          <Box sx={(theme) => ({ bgcolor: theme.palette.grey[50], border: `0.8px solid ${theme.palette.grey[200]}`, borderRadius: '16px', p: { xs: '14px', sm: '20px' } })}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
              <EmailOutlined sx={{ fontSize: 15, color: '#001d2d' }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#001d2d' }}>
                Email Verification
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: '#90a1b9' }}>
              Code sent to{' '}
              <Box component="span" sx={{ color: '#45556c', fontWeight: 700 }}>{email}</Box>
            </Typography>

            {/* OTP inputs */}
            <Box sx={{ display: 'flex', gap: { xs: '6px', sm: '8px' }, mt: '24px', justifyContent: 'center' }}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  value={digit}
                  maxLength={1}
                  inputMode="numeric"
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={handleOtpPaste}
                  onFocus={(e) => e.target.select()}
                  className={verifyError ? `${styles.otpInput} ${styles.otpInputError}` : styles.otpInput}
                />
              ))}
            </Box>
          </Box>

          {verifyError && (
            <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.error.main, mt: '10px', textAlign: 'center' })}>
              {verifyError}
            </Typography>
          )}

          {/* Resend */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '16px' }}>
            {countdown > 0 ? (
              <Typography sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[500] })}>
                Resend codes in{' '}
                <Box component="span" sx={{ fontWeight: 700, color: '#4b5563' }}>{countdown}s</Box>
              </Typography>
            ) : (
              <Typography
                onClick={handleResend}
                sx={(theme) => ({
                  fontSize: 12, fontWeight: 600, color: theme.palette.primary.main,
                  cursor: isSending ? 'default' : 'pointer',
                  opacity: isSending ? 0.6 : 1,
                  '&:hover': { opacity: isSending ? 0.6 : 0.8 },
                })}
              >
                {isSending ? 'Sending…' : 'Resend code'}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: '12px', px: '32px', pt: '12px', pb: '24px' }}>
          <Button
            onClick={handleClose}
            sx={(theme) => ({
              flex: 1, borderRadius: '12px',
              border: `1.6px solid ${theme.palette.grey[200]}`, bgcolor: 'transparent',
              color: theme.palette.grey[600], fontWeight: 700, fontSize: 14, textTransform: 'none',
              py: '13px',
              '&:hover': { bgcolor: theme.palette.grey[50], borderColor: theme.palette.grey[300] },
            })}
          >
            Back
          </Button>
          <Button
            onClick={handleVerify}
            disabled={isVerifying}
            sx={(theme) => ({
              flex: 1, borderRadius: '12px',
              bgcolor: theme.palette.primary.main, color: 'white',
              fontWeight: 700, fontSize: 14, textTransform: 'none',
              py: '13px', boxShadow: 'none',
              display: 'flex', alignItems: 'center', gap: '6px',
              '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#93c5fd', color: 'white' },
            })}
          >
            {isVerifying
              ? <CircularProgress size={16} color="inherit" />
              : <><CheckCircleOutlined sx={{ fontSize: 15 }} />{' Verify & Continue'}</>}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
